import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Grid,
    Stack,
    TextField,
} from "@mui/material";
import SearchButton from "./SearchButton";
import SearchIcon from "./icons/SearchIcon";
import { useTranslation } from "react-i18next";
import { FaqTreeList, FaqTreeStyle, FaqTreeType } from "./FaqTreeList";
import { NestedFaqTreeItem } from "../adapters/api";
import { useIsMobile } from "../hooks";

interface Props {
    type: FaqTreeType;
    tree: NestedFaqTreeItem[];
    onSearchForStandard?: () => void;
    onHoverCategory?: (images: string[] | undefined, caption: string | undefined) => void;
}

export type HoverData = {
    images : string[] | undefined,
    caption: string | undefined
}


const NestedFaqTree: React.FC<Props> = ({ tree, type, onSearchForStandard, onHoverCategory }) => {
    let navigate = useNavigate();
    const { t } = useTranslation();
    const [textFilter, setTextFilter] = useState("");
    const { isMobile } = useIsMobile();

    return (
        <Box sx={isMobile ? FaqTreeStyle.containerMobile : FaqTreeStyle.container}>
            {/* // box solo mobile con:
            // - barra di ricerca ed eventuale pulsante ricerca standard
            // - riquadro con bordo bianco con l'elenco */}
            {isMobile && (
                <Grid container sx={{ marginBottom: type === FaqTreeType.Software ? "20px" : "0px" }}>
                    {/* barra di ricerca */}
                    <Grid item md={4} sm={6} xs={type === FaqTreeType.Software ? 12 : 7}>
                        <SearchIcon color="secondary" sx={{ mr: 1, my: 0.5 }} />
                        <TextField
                            value={textFilter}
                            onChange={(e) => setTextFilter(e.target.value)}
                            id="input-search"
                            variant="standard"
                            sx={{
                                width: isMobile
                                    ? type === FaqTreeType.Software
                                        ? "80%"
                                        : "60%"
                                    : "80%",
                                "& .MuiInput-underline:before": {
                                    borderBottomColor: "white",
                                },
                                "& .MuiInput-underline:after": {
                                    borderBottomColor:
                                        type === FaqTreeType.Software
                                            ? "software.main"
                                            : "testing.main",
                                },
                            }}
                            inputProps={{style: {color: "white"}}}
                            InputLabelProps={{
                                shrink: true,
                                sx: {
                                    "&.MuiInputLabel-formControl": {
                                        color: "text.primary",
                                    },
                                    "&.MuiInputLabel-shrink": {
                                        color: "text.primary",
                                    },
                                    "&.Mui-focused": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "text.pr",
                                            borderWidth: "3px",
                                        },
                                    },
                                },
                            }}
                        />
                    </Grid>
                    
                    {/* Pulsante ricerca standard (solo pagina testing) */}
                    <Grid item md={8} sm={6} xs={4}>
                        {type === FaqTreeType.Testing && (
                            <SearchButton
                                color={"testing.main"}
                                style={{ marginRight: "20px" }}
                                onClick={() => {
                                    onSearchForStandard!();
                                }}
                            >
                                {t("testing.searchstandard")}
                            </SearchButton>
                        )}
                    </Grid>
                </Grid>
            )}

            <Box sx={isMobile ? FaqTreeStyle.contentMobile : FaqTreeStyle.content} overflow={"scroll"}>
                {
                    !isMobile && 
                        <Stack direction={"row"} justifyContent={"space-between"} width={"100%"}> 
                            <Grid
                                item
                                md={4}
                                sm={6}
                                xs={type === FaqTreeType.Software ? 12 : 7}
                            >
                                <SearchIcon color="secondary" sx={{ mr: 1, my: 0.5 }} />
                                <TextField
                                    value={textFilter}
                                    onChange={(e) => setTextFilter(e.target.value)}
                                    id="input-search"
                                    variant="standard"
                                    sx={{
                                        width: isMobile
                                            ? type === FaqTreeType.Software
                                                ? "80%"
                                                : "60%"
                                            : "80%",
                                        "& .MuiInput-underline:before": {
                                            borderBottomColor: "white",
                                        },
                                        "& .MuiInput-underline:after": {
                                            borderBottomColor:
                                                type === FaqTreeType.Software
                                                    ? "software.main"
                                                    : "testing.main",
                                        },
                                    }}
                                    inputProps={{
                                        style: {
                                            color: "white",
                                        },
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                        sx: {
                                            "&.MuiInputLabel-formControl": {
                                                color: "text.primary",
                                            },
                                            "&.MuiInputLabel-shrink": {
                                                color: "text.primary",
                                            },
                                            "&.Mui-focused": {
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "text.pr",
                                                    borderWidth: "3px",
                                                },
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item>
                                {type === FaqTreeType.Testing && (
                                    <SearchButton
                                        color={"testing.main"}
                                        style={{ marginRight: "20px" }}
                                        onClick={() => {
                                            onSearchForStandard!();
                                        }}
                                    >
                                        {t("testing.searchstandard")}
                                    </SearchButton>
                                )}
                                <SearchButton
                                    color={
                                        type === FaqTreeType.Software
                                            ? "software.main"
                                            : "testing.main"
                                    }
                                    onClick={() => {
                                        if (type === FaqTreeType.Software) {
                                            navigate("/testing");
                                        } else {
                                            navigate("/software");
                                        }
                                    }}
                                >
                                    {type === FaqTreeType.Software ? "TIC - Testing, Inspection & Certification" : "Connective Engineering"}
                                </SearchButton>
                            </Grid>
                        </Stack>
                }
                <Box
                    sx={{
                        "&::-webkit-scrollbar": {
                            borderRadius: 2,
                            width: "0.5em",
                            border: "1px solid white",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "#81BC0047",
                            borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: "#81BC00",
                            borderRadius: "5px",
                            border: "1px solid white",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            background: "#81BC00",
                        },
                        overflow: "auto", 
                        flex: 1,
                        minHeight: 0
                    }}
                >
                    <FaqTreeList textFilter={textFilter} tree={tree} type={type} onHoverCategory={onHoverCategory}/>
                </Box>
            </Box>
        </Box>
    );
};

export default NestedFaqTree;
